/** A course with no recorded fee says so, rather than showing £0. */
function Money({ hit }) {
  if (!hit.fee_known) return <span className="muted">On request</span>;

  const base = `£${hit.tuition_base.toLocaleString()}`;
  const showsOriginal = hit.tuition_currency
    && hit.tuition_currency !== 'GBP'
    && hit.tuition_original;

  if (!showsOriginal) return base;

  return (
    <>
      {base}
      <br />
      <span className="muted" style={{ fontSize: 11 }}>
        {`${hit.tuition_currency} ${Math.round(hit.tuition_original).toLocaleString()}`}
      </span>
    </>
  );
}

/**
 * A course advertised as "3-4 years" is shown as the range it is.
 *
 * Printing a single figure claimed a precision the source does not have.
 */
function Duration({ hit }) {
  if (!hit.duration_known) return <span className="muted">—</span>;

  const min = hit.duration_min_months ?? hit.duration_months;
  const max = hit.duration_max_months ?? hit.duration_months;
  return min === max ? `${min} mo` : `${min}–${max} mo`;
}

export function ResultsTable({ hits, found, onOpen, onApply }) {
  if (found === 0) {
    return (
      <div className="empty">
        <b>No programs match all of these filters</b>
        Remove one of the tags above to widen the search.
      </div>
    );
  }

  // A row opens the full record. `onOpen` gets the id straight off the Typesense
  // hit — the same id the detail API looks the MongoDB document up by.
  const open = (h) => onOpen?.(h.id);

  const handleApply = (e, h) => {
    e.stopPropagation();
    if (onApply) {
      onApply(h);
    } else {
      window.alert(`Application started for ${h.name} at ${h.university_name}`);
    }
  };

  return (
    <table className="results-table">
      <thead>
        <tr>
          <th>Program</th>
          <th>University</th>
          <th>Level</th>
          <th style={{ textAlign: 'right' }}>Duration</th>
          <th style={{ textAlign: 'right' }}>Tuition/yr</th>
          <th>Intake</th>
          <th>Offers</th>
          <th style={{ textAlign: 'right' }}>Actions</th>
        </tr>
      </thead>
      <tbody>
        {hits.map((h) => (
          <tr
            key={h.id}
            className={onOpen ? 'row-open' : undefined}
            tabIndex={onOpen ? 0 : undefined}
            role={onOpen ? 'button' : undefined}
            aria-label={onOpen ? `Open ${h.name}` : undefined}
            onClick={onOpen ? () => open(h) : undefined}
            onKeyDown={onOpen ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(h); }
            } : undefined}
          >
            <td data-label="Program">
              <strong className="prog-table-title">{h.name}</strong>
              {h.city && (
                <>
                  <br />
                  <span className="muted" style={{ fontSize: 11.5 }}>{h.city}</span>
                </>
              )}
            </td>
            <td data-label="University">
              <span className="prog-table-uni">{h.university_name}</span>
              <br />
              <span className="muted" style={{ fontSize: 11.5 }}>{h.country_name}</span>
            </td>
            <td data-label="Level">
              <span className="level-badge">{h.degree_group}</span>
            </td>
            <td className="num" data-label="Duration"><Duration hit={h} /></td>
            <td className="num" data-label="Tuition/yr"><Money hit={h} /></td>
            <td data-label="Intake">
              {(h.intakes ?? []).length > 0
                ? (h.intakes ?? []).slice(0, 2).map((i) => <div key={i} className="intake-item">{i}</div>)
                : <span className="muted" title="Intake data not available for this course">—</span>}
            </td>
            <td data-label="Offers">
              <div className="table-offers">
                {h.scholarship && <span className="badge badge-scholarship">Scholarship</span>}
                {h.free_to_apply && <span className="badge badge-free b">Free to apply</span>}
              </div>
            </td>
            <td data-label="Actions" className="table-actions-cell" onClick={(e) => e.stopPropagation()}>
              <div className="row-action-btns">
                <button
                  type="button"
                  className="btn-table-details"
                  onClick={() => open(h)}
                  aria-label={`View details for ${h.name}`}
                >
                  VIEW DETAILS
                </button>
                <button
                  type="button"
                  className="btn-table-apply"
                  onClick={(e) => handleApply(e, h)}
                  aria-label={`Apply for ${h.name}`}
                >
                  APPLY NOW
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
